/**
 * Account-scoped device state: what this browser keeps for the signed-in
 * account outside the in-memory app, and when it is released
 * (ADR-WOA1).
 *
 *   - Provider onboarding drafts (personal data the applicant typed): one key
 *     per account with a 7-day lifetime. Removed on sign-out (explicit, in
 *     another tab, or a session revoked while the console is open) and
 *     whenever another account signs in. A cold start without a session keeps
 *     them: that start may be an offline refresh failure, the drafts are only
 *     readable by their own account, and they expire on their own.
 *   - The browser's Web Push subscription for instant alerts: owned by the
 *     account that turned alerts on (recorded in PUSH_OWNER_STORAGE_KEY).
 *     Explicit sign-out removes it server-side and in the browser. Any other
 *     end of the session (another tab, revocation, account deletion, another
 *     account signing in, or a start-up with no stored session) releases it in
 *     the browser, so the provider's offers stop arriving on this device.
 *
 * This module is imported by the app shell, so it stays tiny: the push
 * mechanics are loaded on demand, only when this browser has push state.
 */
import type { Session } from "@supabase/supabase-js";

import { clearProviderOnboardingDrafts } from "./provider-onboarding-draft";

/** Owner marker for this browser's push subscription: { v, userId, endpointHash, at }. */
export const PUSH_OWNER_STORAGE_KEY = "tinkr.push.owner";
export const PUSH_SERVICE_WORKER_URL = "/push-sw.js";
/**
 * Longest an explicit sign-out waits for the push release (PUX-I-1). The
 * browser unsubscribe starts first and is local; the server removal is
 * hygiene that the push service's "gone" answer also achieves.
 */
export const SIGN_OUT_DEVICE_RELEASE_BOUND_MS = 800;

/** The account recorded as owning this browser's push subscription, if any. */
export function readPushOwnerUserId(): string | null {
  try {
    const raw = window.localStorage.getItem(PUSH_OWNER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId?: unknown } | null;
    return typeof parsed?.userId === "string" && parsed.userId ? parsed.userId : null;
  } catch {
    return null;
  }
}

/** Release a push subscription recorded for an account other than `userId` (null: any account). */
function releasePushNotOwnedBy(userId: string | null): void {
  const owner = readPushOwnerUserId();
  if (!owner || owner === userId) return;
  void import("./push-notifications")
    .then((push) => push.releasePushForOtherAccount(userId))
    .catch(() => undefined);
}

// undefined: nothing applied yet on this page (cold start).
let reconciledFor: string | null | undefined;
let retainNextSignOut = false;

/**
 * Called whenever the app applies a session (sign-in, restore, account
 * switch, sign-out). Idempotent per account, cheap when nothing is stored.
 */
export function reconcileAccountDeviceState(userId: string | null): void {
  if (reconciledFor === userId) return;
  const previous = reconciledFor;
  reconciledFor = userId;
  if (userId) {
    clearProviderOnboardingDrafts(userId);
    releasePushNotOwnedBy(userId);
    return;
  }
  // No session. Only an account that was signed in on this page and is now
  // gone has signed out (I-COR-1, WOA1-R2); a cold start is left to
  // releaseDeviceStateWithoutSession, which knows whether a session exists.
  if (typeof previous !== "string") return;
  if (retainNextSignOut) {
    retainNextSignOut = false;
    return;
  }
  clearProviderOnboardingDrafts(null);
  releasePushNotOwnedBy(null);
}

/**
 * The password-setup flow signs out only to make the same user sign in again
 * with the new password on this device: keep its device state through the
 * SIGNED_OUT that follows.
 */
export function retainAccountDeviceStateOnNextSignOut(): void {
  retainNextSignOut = true;
}

/** Forget a retain request that no sign-out consumed (for example a failed sign-out). */
export function clearRetainedDeviceState(): void {
  retainNextSignOut = false;
}

/**
 * Start-up found no stored session on this browser (not an offline refresh
 * failure, which reports an error instead). Instant alerts an account left
 * behind are released: their session ended while no console was open (revoked
 * elsewhere, account deleted). Drafts stay; they are per account and expire.
 */
export function releaseDeviceStateWithoutSession(): void {
  releasePushNotOwnedBy(null);
}

/**
 * Release this account's device state before an explicit sign-out, while the
 * session can still authorize the server-side removal. Bounded so sign-out is
 * never held hostage by a slow network.
 */
export async function releaseAccountDeviceState(
  session: Session | null,
  timeoutMs = SIGN_OUT_DEVICE_RELEASE_BOUND_MS
): Promise<void> {
  clearProviderOnboardingDrafts(null);
  if (!session || !(await hasPushRegistration())) return;
  const release = import("./push-notifications")
    .then((push) => push.releaseProviderPushOnSignOut(session))
    .catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    release,
    new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); })
  ]);
  if (timer) clearTimeout(timer);
}

/** True when this browser has the push service worker registered (no network). */
export async function hasPushRegistration(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
    return Boolean(await navigator.serviceWorker.getRegistration(PUSH_SERVICE_WORKER_URL));
  } catch {
    return false;
  }
}

/** Test seam: forget which account was last reconciled. */
export function resetAccountDeviceStateForTests(): void {
  reconciledFor = undefined;
  retainNextSignOut = false;
}
